import '../../stylesheets/dashboards/dashboards.css'
import '../../stylesheets/dashboards/transaction-comps.css'
/* == React == */
import { useState, useEffect, useRef }                                      from 'react'
import { useOutletContext }                                                 from 'react-router-dom'
/* == Components == */
import ChartsSkeleton                                                       from '../skeletons/ChartsSkeleton.js'
import MetricPaletteSections                                                from './benchmarking-charts-shared/MetricPaletteSections.js'
import Chart                                                                from 'chart.js/auto';
import ChartDataLabels                                                      from 'chartjs-plugin-datalabels';
/* == Contstants == */
import { SELECTION_UPPER_LIMIT, PC_TOOLTIP_TILE_COMPANION_DICTIONARY,
         Y_AXIS_GUTTER_PX, Y_AXIS_GUTTER_MOBILE_PX }                        from '../../_constants/public-comparables.js'
import { MINI_IPAD }                                                        from '../../_constants/break-points.js'
import { COMPS_TILE_SECTIONS_LIST }                                         from '../../_constants/public-comparables'
import { usePublicAPI }                                                     from '../../hooks/external/proprietary/usePublicAPI.js'
import { useViewportWidth }                                                 from '../../hooks/internal/ui/useViewportWidth.js'

import { orchestrateCompanion, orchestrateYAxis,
         orchestrateFooterCompanion }                                       from '../../services/benchmarkCharts/main/main.js'
import { checkViewportWidthLessThan }                                       from '../../utilities/viewport.js'
import { formatNumber, formatNumberOneDecimal }                             from '../../utilities/formatNumber.js';
import { filterManyByIds, findOneById }                                     from '../../utilities/idResolution.js';
import { createIdColorDictionary, chartColorsService }                      from '../../services/benchmarkCharts/main/main.js';
import { median }                                                           from "../../utilities/statistics.js"
import { parseToNumericOnly }                                               from '../../utilities/sanitize.js'

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const PublicComparables = () => {
    // ==== Hooks ==== //
    const { getCoLogo } = usePublicAPI();
    const viewportWidth = useViewportWidth();

    // ====== State ====== //
    // Lifted React State
    const {
        compsCategories,
        selectedCompsCategoryId,
        onCompsCategorySelectionChange,
        companies,
        selectedCompanyIds,
        onCompanySelectionChange,
        compsTiles,
        selectedCompsTileIds,
        onCompsTileSelectionChange,
        compsIsLoading
    } = useOutletContext()

    // Local React State
    const [draggedTileId, setDraggedTileId]         = useState(null);
    const [draggedFromTray, setDraggedFromTray]     = useState(null); // Track if dragging from tray
    const [ghostPosition, setGhostPosition]         = useState(null); // Track touch coordinates for ghost element
    const [isOffcanvasOpen, setIsOffcanvasOpen]     = useState(false);
    const [isDropdownOpen, setIsDropdownOpen]       = useState(false);
    const [searchTerm, setSearchTerm]               = useState('');

    const chartRefs                                 = useRef([null, null]);
    const chartInstance0                            = useRef(null);
    const chartInstance1                            = useRef(null);
    const chartInstanceRefs                         = [chartInstance0, chartInstance1];

    // Local Variables
    const isViewportWidthMobileBrowser              = checkViewportWidthLessThan(viewportWidth, MINI_IPAD)                       // returns value of true or false into variable declaration to use to ternary for two way branch conditional render of different font size, label rotation, etc in chart config object for chart build
    const colorDictionary                           = createIdColorDictionary(selectedCompanyIds);

    // ==== Logic ==== //
    const updateCharts = () => {
        selectedCompsTileIds.forEach((tileId, index) => {
            if (tileId !== null && chartRefs.current[index]) {
                const tile = compsTiles.find(t => t.id === tileId);
                createChart({ current: chartRefs.current[index] }, chartInstanceRefs[index], tile, companies, index);
            }
        });
    };

    useEffect(() => {
        // Update charts when:
        // 1) tiles in tray array of metric control panel change
        // 2) there is change in companies selected from offcanvas company selector
            // a. and when a new category is selected there is always a change in the companies selected!
        // 3) When viewport width is below certain threshold (mobile browser), so we can re-print bar labels in chart on slightly slanted axis, so they dont collide
        updateCharts();
    }, [selectedCompanyIds, selectedCompsTileIds, isViewportWidthMobileBrowser]);

    // ** API Loading Gate **
    if (compsIsLoading) return <ChartsSkeleton />

    // ** Mouse Event Handlers Called by Mouse Event Listeners **
    const handleDragStart = (e, tileId, fromTrayIndex = null) => {
        setDraggedTileId(tileId);
        setDraggedFromTray(fromTrayIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e) => {
        //e.preventDefault(); // can remove: since not enough on its own to prevent browser souce code from blocking drop since onDragOver fires every 50ms and will re-block after
        e.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleTrayDrop = (e, index) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        
        if (draggedTileId) {
            const newSelectedTileIds = [...selectedCompsTileIds];
            
            // If dragging from one tray slot to another, swap the tile IDs
                // 1) draggedFromTray state is how we track if dragged from component tray or metric palette
                // 2) then check to make sure its not running for dropping into the same tray its already in, only run logic if draggedFromTray is not equal to index of current tray dropped into
            if (draggedFromTray !== null && draggedFromTray !== index) {
                const existingTileId = newSelectedTileIds[index];
                newSelectedTileIds[index] = draggedTileId;
                newSelectedTileIds[draggedFromTray] = existingTileId;
                onCompsTileSelectionChange(newSelectedTileIds);
            }
            // If dragging from outside the tray slots, replace the existing tile ID
            else if (draggedFromTray === null) {
                newSelectedTileIds[index] = draggedTileId;
                onCompsTileSelectionChange(newSelectedTileIds);
            }
            // else: Dragging from tray back to same slot (no-op, so don't call state setter)
            
            setDraggedTileId(null);
            setDraggedFromTray(null);
        }
    };

    const handlePaletteDrop = (e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        
        if (draggedTileId && draggedFromTray !== null) {
            const newSelectedTileIds = [...selectedCompsTileIds];
            newSelectedTileIds[draggedFromTray] = null;
            onCompsTileSelectionChange(newSelectedTileIds);
        }
        
        setDraggedTileId(null);
        setDraggedFromTray(null);
    };

    // ** Touch event handlers called by touch event listeners - exactly match mouse handler logic **
    const handleTouchStart = (e, tileId, fromTrayIndex = null) => {
        // Canonical place for setting initial value of "tracking" state variables on the source side
        setDraggedTileId(tileId);
        setDraggedFromTray(fromTrayIndex);
        // Initialize ghost position immediately at touch start point
        const touch = e.touches[0];
        setGhostPosition({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchMove = (e) => {
        // Canonical place for editing /overriding browser infra and UI/dynamic visual feedback updates 
        e.preventDefault(); // Prevent scrolling while dragging
        if (draggedTileId) {
            const touch = e.touches[0];
            setGhostPosition({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();
        
        // Early exit if nothing is being dragged, this stateful variable is fired by the onDragStart listeners handler
        // and the listener is only hung on certain elements we want to tag a "touch draggable"
        if (!draggedTileId) {
            return;
        }
        
        // Get the touch point coordinates on DOM from exact point where user lifted finger (i.e. where they "dropped" the tile)
        const touch = e.changedTouches[0];
        
        // Find which DOM element is at those exact coordinates
        // This is how touch detects "drop targets" (no automatic detection like mouse drag-and-drop)
        const dropTargetElement = document.elementFromPoint(touch.clientX, touch.clientY);
        
        // If no element found (finger lifted outside browser), just reset state and early return
        if (!dropTargetElement) {
            setDraggedTileId(null);
            setDraggedFromTray(null);
            return;
        }
        
        // Detect which drop zone the tile was dropped on
        const componentTraySlot = dropTargetElement.closest('.component-tray');
        const metricPaletteContainer = dropTargetElement.closest('.tiles-grid');
        
        // Case 1: Dropped on component tray slot
        if (componentTraySlot) {
            const componentTrayTargetIndex = parseInt(componentTraySlot.getAttribute('data-tray-index'));
            
            if (componentTrayTargetIndex === 0 || componentTrayTargetIndex === 1) {
                const newSelectedTileIds = [...selectedCompsTileIds];
                
                // Scenario A: Swap tiles between tray slots
                if (draggedFromTray !== null && draggedFromTray !== componentTrayTargetIndex) {
                    const existingTileId = newSelectedTileIds[componentTrayTargetIndex];
                    newSelectedTileIds[componentTrayTargetIndex] = draggedTileId;
                    newSelectedTileIds[draggedFromTray] = existingTileId;
                    onCompsTileSelectionChange(newSelectedTileIds);
                }
                // Scenario B: Add tile from palette to tray
                else if (draggedFromTray === null) {
                    newSelectedTileIds[componentTrayTargetIndex] = draggedTileId;
                    onCompsTileSelectionChange(newSelectedTileIds);
                }
                // Scenario C: Dropped on same tray slot (no-op, don't call state setter)
            }
        }
        
        // Case 2: Dropped in metric palette container
        if (metricPaletteContainer) {
            // Case 2 Scenario A: Only remove tile id from our tray state tracking variable if tile came from a tray and was dropped in the mettric palette container (not palette-to-palette)
            if (draggedFromTray !== null) {
                const newSelectedTileIds = [...selectedCompsTileIds];
                newSelectedTileIds[draggedFromTray] = null;
                onCompsTileSelectionChange(newSelectedTileIds);
            }
            // Case 2 Scenario B: if dragged from tray is null it means the tile didnt come from the tray and means it came from the palette
                // since those are the only two elements we call the onDragStart on and therefore can get into this logic to start from one of those two elements being touch dragged via the statful draggedTileId varible
               // it means if it didnt come from the tray it came from the palette and we dont need to do anything
        }
        
        
        // Case 3: Dropped somewhere else not in either of our handled for drop zones (invalid drop zone)
        // dont run any logic
        
        // Cleanup: Always reset drag state after touch ends
        setDraggedTileId(null);
        setDraggedFromTray(null);
        setGhostPosition(null);
    };

    // **=========== INSTANTIATE CHART INSTANCE ===========** //
    const createChart = (canvasRef, chartInstanceRef, tile, companies, index) => {
        if (!canvasRef.current || !tile || companies.length === 0) {
            return;
        }

        const ctx = canvasRef.current.getContext('2d');
        
        // Destroy existing chart
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        // **---------- Sort selectedCompanyIds by metric value (highest to lowest) with N/A's last ----------** //
        const orderedSelectedCompanyIds = [...selectedCompanyIds].sort((a, b) => {
            const companyA = findOneById(companies, a);
            const companyB = findOneById(companies, b);
            // Push N/A values to the end
            if (companyA[tile.field] === 'N/A') return 1;
            if (companyB[tile.field] === 'N/A') return -1;
            // Sort numeric values highest to lowest
            return companyB[tile.field] - companyA[tile.field];
        });

        // **---------- Company Values of Charted Metric (Tile Field) w/ N/A's Preserved ----------** //        
        // dbValuesForField: Central Location / Var To Hold / Store Native Object (i.e. vaules on keys to still be 'N/A') for usage in data labels and tooltips, so the "N/A" is rendered in data labels and tool tips
        // because for the chart data we have to coerce the N/A's to a pseudo-value of 0 since actual data has to take a number, but we want the label to show N/A and therefore use this array below for the labels, and the coerced N/A -> 0 array for the data!
        // Note: can probably work on the semantics of this variable name to make it like 'datalabels' or something juxtaposed with the below 'data' variable declaration
        const dbValuesForField = orderedSelectedCompanyIds.map(id => {
            const company = findOneById(companies, id);
            return company[tile.field];                    // return the value of a field from that company object with id matching the id (element) we are currently on in loop over array 
        });     

        // **---------- For actual data we chart - must Convert 'N/A' values to 0 so bars visually display ----------** //
        // [NOTE 1]: For actual data we chart - must Convert 'N/A' values to 0 so bars visually display (while still allowing labels/tooltips use dbValuesForField to show 'N/A' gracefully!)
        // [NOTE 2]: Chart.js only renders bars for numeric data types. If we passed 'N/A' strings directly, those companies would have no bar at all. By converting 'N/A' -> 0, we ensure a bar renders (at 0 height) so the company stays visible with its 'N/A' label and tooltip.
        const data = dbValuesForField.map(value => value === 'N/A' ? 0 : value)
        // [NOTE 3]: Required to be in config for chart js library to work, but not actually used in what we visually render: see top level data prop below, and then nested 'labels' prop in chart config object
        const labels = orderedSelectedCompanyIds.map(id => {
            const company = findOneById(companies, id);
            return company.ticker;
        })

        // **---------- CHART INSTANCE CONFIG ----------** //
        chartInstanceRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: labels, // this is the x-axis label (which we arent using - display: false later in the config - but this field is required to include in the config anyway)
                datasets: [
                    {
                        label: tile.name,
                        data: data,
                        backgroundColor: chartColorsService(colorDictionary, orderedSelectedCompanyIds),
                        borderWidth: 0,
                        borderRadius: 4,
                        borderSkipped: false,
                        order: 0
                    },
                    {
                        label: 'Median',
                        type: 'line',
                        data: Array(selectedCompanyIds.length).fill( median(parseToNumericOnly(dbValuesForField)) ),
                        datalabels: { display: false },   // ← add; hides labels for this dataset only                        
                        borderColor: 'rgba(101, 124, 178, 0.7)',
                        borderWidth: 3,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        order: 1
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false
                    },
                    datalabels: {
                        color: '#666',
                        font: {
                          size: isViewportWidthMobileBrowser ? 10 : 11,
                          family: 'Arial'
                        },
                        anchor: "end",
                        align: isViewportWidthMobileBrowser ? -70 : "end",
                        display: true,
                        rotation: isViewportWidthMobileBrowser ? -30 : 0,
                        formatter: (value, context) => {
                            const percentageFields      = ['currentRevenueGrowth', 'forwardRevenueGrowth', 'ttmGrossMargin', 'ttmEbitdaMargin', 'currentEarningsGrowth', 'forwardEarningsGrowth', 'ruleOf40', 'dividendYield', 'netDebtToMarketEquity'];
                            const amountFields          = ['marketCapitalization', 'enterpriseValue'];
                            const multipleFields        = ['ttmRevenueMultiple', 'currentYearRevenueMultiple', 'nextYearRevenueMultiple', 'ttmEbitdaMultiple', 'ttmPeMultiple', 'currentYearPeMultiple', 'nextYearPeMultiple'];

                            // Get original value from filteredCompanies (preserves N/A)
                            const originalValue = dbValuesForField[context.dataIndex];

                            // If N/A, return just 'N/A' without field-specific formatting
                            if (originalValue === 'N/A') return 'N/A';

                            if(percentageFields.includes(tile.field)) return `${formatNumberOneDecimal(originalValue)}%`
                            if(amountFields.includes(tile.field)) return `$${formatNumberOneDecimal(originalValue)}B`
                            if(multipleFields.includes(tile.field)) return `${formatNumberOneDecimal(originalValue)}x`
                            return formatNumberOneDecimal(originalValue)
                        }
                    },
                    tooltip: {
                        footerFont: { weight: 'normal' },   // library defaults the footer to bold
                        callbacks: {
                            label: function(context) {
                                // Get original value from filteredCompanies (preserves N/A)
                                const originalValue = dbValuesForField[context.dataIndex];

                                // **==== SECTION: FORMARTTING VALUES DISPLAYED IN TOOLTIP ====** //
                                // -- FORMATTING DICTIONARIES -- //
                                const percentageFields      = ['currentRevenueGrowth', 'forwardRevenueGrowth', 'ttmGrossMargin', 'ttmEbitdaMargin', 'currentEarningsGrowth', 'forwardEarningsGrowth', 'ruleOf40', 'dividendYield', 'netDebtToMarketEquity'];
                                const amountFields          = ['marketCapitalization', 'enterpriseValue'];
                                const multipleFields        = ['ttmRevenueMultiple', 'currentYearRevenueMultiple', 'nextYearRevenueMultiple', 'ttmEbitdaMultiple', 'ttmPeMultiple', 'currentYearPeMultiple', 'nextYearPeMultiple'];                                
                                // -- FORMATTING APPLICATION -- // 
                                // If N/A, return just 'N/A' without field-specific formatting
                                if (originalValue === 'N/A')                        return 'N/A';
                                // Field Specific Formatting for Charted Metric Tile Values
                                if (tile && percentageFields.includes(tile.field))  return `${formatNumberOneDecimal(originalValue)}%`;
                                if (tile && amountFields.includes(tile.field))      return `$${formatNumberOneDecimal(originalValue)}B`;
                                if (tile && multipleFields.includes(tile.field))    return `${formatNumberOneDecimal(originalValue)}x`;
                                return formatNumberOneDecimal(originalValue);
                            },
                            // afterBody (rather than afterLabel) so the companion line renders flush left, visually separated from the hovered value rather than indented under its color box
                            afterBody: function(tooltipItems) {
                                // **--------- Returns a string to get rendered into the afterBody slot in the tooltip scaffolding provided by the library ----------** //
                                //┌─────────────────────────┐
                                //│ beforeTitle             │
                                //│ title         (bold)    │  title section, once per tooltip
                                //│ afterTitle              │
                                //│                         │
                                //│ beforeBody              │  once per tooltip, flush left
                                //│   beforeLabel           │
                                //│ ■ label                 │  once per hovered item, indented with svg beforehand
                                //│   afterLabel            │
                                //│ afterBody     ← yours   │  once per tooltip, flush left
                                //│                         │
                                //│ beforeFooter            │
                                //│ footer        (bold)    │  footer section, once per tooltip
                                //│ afterFooter             │
                                //└─────────────────────────┘                         
                                
                                // **---------- Tooltip Companion: For Some Tile's We Are Going To Display a 'Companion Field's' Value As Additional Data in the Tooltip for That Company's Bar ----------** //
                                const barIndex = tooltipItems[0].dataIndex               
                                return orchestrateCompanion(PC_TOOLTIP_TILE_COMPANION_DICTIONARY, tile, orderedSelectedCompanyIds, barIndex, companies)
                            },
                            footer: function(tooltipItems) {
                                // **---------- Tooltip Footer: Company Name of the Bar Being Hovered ----------** //
                                const barIndex = tooltipItems[0].dataIndex
                                return orchestrateFooterCompanion("company", orderedSelectedCompanyIds, barIndex, companies)
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        afterFit: (scale) => { scale.width = isViewportWidthMobileBrowser ? Y_AXIS_GUTTER_MOBILE_PX : Y_AXIS_GUTTER_PX }, // pins the gutter so the plot area's left edge is a constant across metrics, which is what the logo row's margin-left keys off of; mobile gets a narrower one since the tick font drops to 10
                        min: orchestrateYAxis(dbValuesForField).min, // change this to pass a "min" / "max" key into each, so we can show which we are running in logs
                        max: orchestrateYAxis(dbValuesForField).max, // change this to pass a "min" / "max" key into each, so we can show which we are running in logs
                        grid: {
                            display: false
                        },
                        ticks: {
                            font: {
                                size: isViewportWidthMobileBrowser ? 10 : 11
                            },
                            precision: 0,        // calls to .toFixed(0) internally on each index value (tick value); which rounds tick index value to the nearest integer
                            callback: function(value, index, ticks) {
                                const firstTick  = index === 0
                                const middleTick = index === (Math.floor(ticks.length/2)) 
                                const lastTick   = index === ticks.length - 1

                                const percentageFields      = ['currentRevenueGrowth', 'forwardRevenueGrowth', 'ttmGrossMargin', 'ttmEbitdaMargin', 'currentEarningsGrowth', 'forwardEarningsGrowth', 'ruleOf40', 'dividendYield', 'netDebtToMarketEquity'];
                                const amountFields          = ['marketCapitalization', 'enterpriseValue'];
                                const multipleFields        = ['ttmRevenueMultiple', 'currentYearRevenueMultiple', 'nextYearRevenueMultiple', 'ttmEbitdaMultiple', 'ttmPeMultiple', 'currentYearPeMultiple', 'nextYearPeMultiple'];
                                
                                if (firstTick || middleTick || lastTick) {
                                    if (percentageFields.includes(tile.field)) return `${formatNumber(value)}%`
                                    if (amountFields.includes(tile.field)) return `$${formatNumber(value)}B`
                                    if (multipleFields.includes(tile.field)) return `${formatNumber(value)}x`
                                    return formatNumber(value)
                                }
                                return ""
                            }
                        },
                    },
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            display: false
                        }
                    }
                },
                layout: {
                    padding: {
                        top: 30,
                        right: isViewportWidthMobileBrowser ? 14 : 0
                    }
                }
            },
        });
    };

    // **=========== JSX Section Helper Functions ===========** //
    const toggleCompanySelection = (companyId) => {
        if (selectedCompanyIds.includes(companyId)) {
            // Deselect the company from company display section for charting if id of company selected already in array of selectedCoIds
            onCompanySelectionChange(selectedCompanyIds.filter(id => id !== companyId));
        }
        
        if (!selectedCompanyIds.includes(companyId) && selectedCompanyIds.length < SELECTION_UPPER_LIMIT) {
            // If id of company selected not already in selection & number selected under selection upper limnit, add to selection
            onCompanySelectionChange([...selectedCompanyIds, companyId]);
        }
    };

    const removeCompanySelection = (companyId) => {
        const coIds = selectedCompanyIds.filter(id => id !== companyId)
        console.log(`[SUCCESS]: Now ${coIds.length} company(s) selected: ${coIds}`)
        onCompanySelectionChange(coIds)

    };

    const filteredCompanies = companies.filter(company => 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.ticker.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const availableTiles = compsTiles.filter(tile => 
        !selectedCompsTileIds.includes(tile.id)
    );

    // **=========== JSX RETURN/RENDER SECTION: EVENTUALLY RESOLVED TO A PIECE OF RENDERED HTML TREE WHEN CODE TRANSPILED TO JS, AND JS CODE RUN ===========** //
    return (
        <div className="page-wrapper">
            <div className="dashboard">
                {/* 1. Left Panel - Controller */}                
                <div className="left-panel">
                    {/* Header Section */}
                    <div className="left-panel-header-container">
                        <h2 className="panel-title">Company Metrics</h2>
                    </div>

                    {/* Body Section: Metric Control Panel */}
                    <div className="left-panel-body-container">

                        {/* Body Section 1: Component Trays for dropping metric tiles from metric palette */}
                        <div className="component-tray-container">
                            {[0, 1].map(index => (
                                <div
                                    key={index}
                                    className={`component-tray ${selectedCompsTileIds[index] !== null ? 'occupied' : ''} ${draggedTileId === selectedCompsTileIds[index] ? 'dragging' : ''}`} // the third stack "dragging" is very light css addition to just give slight opacity if drag event has begun for this tile our of tray
                                    data-tray-index={index}
                                    draggable={selectedCompsTileIds[index] === null ? false : true}
                                    onDragStart={selectedCompsTileIds[index] === null ? null : (e) => handleDragStart(e, selectedCompsTileIds[index], index)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleTrayDrop(e, index)}
                                    // Mouse family side Optional: just helpful for UI state updates for user when entering and leaving this element with a dragged item
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}

                                    onTouchStart={selectedCompsTileIds[index] === null   ? undefined : (e) => handleTouchStart(e, selectedCompsTileIds[index], index)}
                                    onTouchMove={selectedCompsTileIds[index]  === null   ? undefined : handleTouchMove}
                                    onTouchEnd={selectedCompsTileIds[index]   === null   ? undefined : handleTouchEnd}
                                >
                                    {selectedCompsTileIds[index] === null ? (
                                        <div className="component-tray-text">
                                            Drop metric {index + 1} here
                                        </div>
                                    ) : (
                                        <div className="component-tray-text">
                                            {findOneById(compsTiles, selectedCompsTileIds[index]).name}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Body Section 2: Metric Pallette with Tiles to Drop into Trays */}
                        <div className="transaction-tiles-container">
                            <div 
                                className="tiles-grid"
                                onDragOver={handleDragOver}
                                onDrop={handlePaletteDrop}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                            >
                                <MetricPaletteSections
                                   sections            =    {COMPS_TILE_SECTIONS_LIST}
                                   tiles               =    {compsTiles}
                                   selectedTileIds     =    {selectedCompsTileIds}                                   
                                   draggedTileId       =    {draggedTileId}
                                   handleDragStart     =    {handleDragStart}
                                   handleTouchStart    =    {handleTouchStart}
                                   handleTouchMove     =    {handleTouchMove}
                                   handleTouchEnd      =    {handleTouchEnd}
                                />                                                          
                            </div>
                        </div>
                    </div>
                </div>
                    
                {/* 2. Right Panel - Charts */}
                <div className="right-panel">

                    {/* Header Section */}
                    <div className="right-panel-header-container">
                        <h2 className="panel-title">Company Profiles</h2>
                        <div className="right-panel-benchmarking-charts-controls-container">
                            <button
                                className="selector-btn"
                                onClick={() => setIsOffcanvasOpen(true)}
                            >
                                <svg className="edit-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit Selection
                            </button>
                        </div>
                    </div>
                    
                    {/* Body (charting) Section */}   
                    <div className="charts-container">
                        {[0,1].map((element, index) => 
                            selectedCompsTileIds[index] !== null ? (
                                <div className="chart-section">
                                    {/* title */}
                                    <h3 className="chart-title">{findOneById(compsTiles, selectedCompsTileIds[index]).name}</h3>
                                    {/* chart */}
                                    <div className="chart-container">
                                        <canvas ref={el => chartRefs.current[index] = el}></canvas>
                                    </div>
                                    {/* print logos corresponding to bars in chart */}
                                    <div className="charted-companies-grid-container">
                                        {[...selectedCompanyIds]
                                            .sort((a, b) => {
                                                // match id of first index of pair of selectedCompsTileIds with id of tiles list, and then take the field name from that tile in the list
                                                const sortByFieldValue = findOneById(compsTiles, selectedCompsTileIds[index]).field;
                                                const companyA = findOneById(companies, a);
                                                const companyB = findOneById(companies, b);
                                                // Push N/A values to the end (same logic as chart sort)
                                                if (companyA[sortByFieldValue] === 'N/A') return 1;
                                                if (companyB[sortByFieldValue] === 'N/A') return -1;
                                                // Sort numeric values highest to lowest
                                                return companyB[sortByFieldValue] - companyA[sortByFieldValue];
                                            })
                                            .map((id) => {
                                                const company = findOneById(companies, id);
                                                return (
                                                    <div key={company.id} className="charted-company-logo-card">
                                                        <img
                                                            src={getCoLogo(company.ticker)}
                                                            alt={company.name}
                                                            className="logo-card-company-logo"
                                                            title={company.name}
                                                        />
                                                        <p className="logo-card-company-ticker charted">{company.ticker}</p>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            ) : (
                                <div className="chart-section">
                                    <div className="empty-state">
                                        {`Metric tile ${index + 1} from palette charted here`}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
                
                {/* Off Canvas Elements Section */}
                {/* 3. Offcanvas Overlay */}
                <div 
                    className={`offcanvas-overlay ${isOffcanvasOpen ? 'open' : ''}`}
                    onClick={() => setIsOffcanvasOpen(false)}
                ></div>

                {/* 4. Offcanvas Panel */}
                <div className={`offcanvas ${isOffcanvasOpen ? 'open' : ''}`}>

                    {/* Header */}
                    <div className="offcanvas-header">
                        <h3 className="offcanvas-title">Company Selector</h3>
                        <button 
                            className="close-btn"
                            onClick={() => setIsOffcanvasOpen(false)}
                        >
                            ×
                        </button>
                    </div>

                    {/* Body */}
                    <div className="offcanvas-body">

                        {/* Sector Dropdown Element Container */}
                        <div className="sector-dropdown-container">
                            <div className="sector-dropdown">
                                {/* Drop down button */}
                                <button 
                                    className="sector-dropdown-button"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <div className="dropdown-content">
                                        <span className="dropdown-label">Select Sector</span>
                                        <div className="dropdown-value-wrapper">
                                            <div className="dropdown-button-icon"></div>
                                            <span className="dropdown-value">{compsCategories.find(c => c.id === selectedCompsCategoryId)?.name || 'Choose an option'}</span>
                                        </div>
                                    </div>
                                    <svg className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>

                                {/* Drop down menu */}
                                {isDropdownOpen && (
                                    <div className="sector-dropdown-menu">
                                        {/* Printing List Items */}
                                        {compsCategories.map(category => (
                                            <div 
                                                key={category.id}
                                                className={`sector-dropdown-item ${selectedCompsCategoryId === category.id ? 'selected' : ''}`}
                                                onClick={() => {
                                                    onCompsCategorySelectionChange(category.id);
                                                    setSearchTerm("")
                                                    console.log("[SUCCESS]: Category updated to number: ", category.id)
                                                    setIsDropdownOpen(false);
                                                }}
                                            >   
                                                {category.isHot && (
                                                    <div className="sector-icon"></div>
                                                )}
                                                <span className="sector-name">{category.name}</span>
                                                {selectedCompsCategoryId === category.id && (
                                                    <svg className="checkmark-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Search */}
                        <div className="search-container">
                            <div className="search-input-wrapper">
                                <div className="search-content">
                                    <span className="search-label">Company Search</span>
                                    <div className="dropdown-value-wrapper">
                                        <div className="search-icon"></div>
                                        <input
                                            type="text"
                                            className="search-input-field"
                                            placeholder="Search sector for company"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                            
                        {/* Selected Companies */}
                        {selectedCompanyIds.length > 0 && (
                            <div className="selected-companies-section">
                                <h4 className="selected-companies-title">Selected Companies ({selectedCompanyIds.length}/{SELECTION_UPPER_LIMIT})</h4>
                                <div className="selected-companies-grid-container">
                                    {filterManyByIds(companies, selectedCompanyIds).map(company => (
                                        <div key={company.id} className="selected-company-logo-card">
                                            <img
                                                src={getCoLogo(company.ticker)}
                                                alt={company.name}
                                                className="logo-card-company-logo"
                                            />
                                            <div className="selected-company-info">
                                                <span className="logo-card-company-name">{company.name}</span>
                                                <span className="logo-card-company-ticker">({company.ticker})</span>
                                            </div>
                                            <button 
                                                className="remove-company-btn"
                                                onClick={() => removeCompanySelection(company.id)}
                                                title="Remove company"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Available Companies to Select Grid */}
                        <div className="available-companies-grid-container">
                            {filteredCompanies.map(company => (
                                <div
                                    key={company.id}
                                    className={`available-company-logo-card ${
                                        selectedCompanyIds.includes(company.id) ? 'selected' : ''
                                    }`}
                                    onClick={() => toggleCompanySelection(company.id)}
                                >
                                    <img
                                        src={getCoLogo(company.ticker)}
                                        alt={company.name}
                                        className="logo-card-company-logo"
                                    />
                                    <h4 className="logo-card-company-name">{company.name}</h4>
                                    <p className="logo-card-company-ticker">({company.ticker})</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ghost element for touch drag - only renders during touch drag */}
            {draggedTileId && ghostPosition && (
                <div 
                    className="ghost-tile"
                    style={{
                        left: `${ghostPosition.x}px`,
                        top: `${ghostPosition.y}px`
                    }}
                >
                    {compsTiles.find(t => t.id === draggedTileId)?.name || ''}
                </div>
            )}
        </div>
    );
};

export default PublicComparables;
